import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  InviteMemberRequest,
  Member,
  MemberList,
  RemoveMemberResponse,
  ResetMemberPasswordResponse,
  type Role,
  type UpdateMemberRoleRequest,
} from '@rp/contracts'
import { useReadySession } from '../../session/useSession'
import { settingsKeys } from './queries'

const ME_QUERY_ROOT = ['me'] as const

export type RoleChange = { member: Member; role: Role }

const useMemberRefresh = () => {
  const { membership, me } = useReadySession()
  const queryClient = useQueryClient()
  return (userId: string | null) => {
    void queryClient.invalidateQueries({ queryKey: settingsKeys.members(membership.tenantId) })
    if (userId === me.user.id) void queryClient.invalidateQueries({ queryKey: ME_QUERY_ROOT })
  }
}

export const useMembers = () => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: settingsKeys.members(membership.tenantId),
    queryFn: async () => (await api.request(MemberList, { method: 'GET', path: '/v1/users' })).data.members,
  })
}

export const useInviteMember = () => {
  const { api } = useReadySession()
  const refresh = useMemberRefresh()
  return useMutation({
    mutationFn: async (input: InviteMemberRequest) =>
      (await api.request(Member, { method: 'POST', path: '/v1/users', body: input })).data,
    onSuccess: () => refresh(null),
  })
}

export const useChangeMemberRole = () => {
  const { api } = useReadySession()
  const refresh = useMemberRefresh()
  return useMutation({
    mutationFn: async ({ member, role }: RoleChange) =>
      (
        await api.request(Member, {
          method: 'PATCH',
          path: `/v1/users/${member.userId}`,
          body: { role } satisfies UpdateMemberRoleRequest,
        })
      ).data,
    onSettled: (_data, _error, change) => refresh(change.member.userId),
  })
}

export const useRemoveMember = () => {
  const { api } = useReadySession()
  const refresh = useMemberRefresh()
  return useMutation({
    mutationFn: async (member: Member) =>
      (await api.request(RemoveMemberResponse, { method: 'DELETE', path: `/v1/users/${member.userId}` })).data,
    onSettled: (_data, _error, member) => refresh(member.userId),
  })
}

export type PasswordReset = { member: Member; password: string }

export const useResetMemberPassword = () => {
  const { api } = useReadySession()
  return useMutation({
    mutationFn: async ({ member, password }: PasswordReset) =>
      (
        await api.request(ResetMemberPasswordResponse, {
          method: 'POST',
          path: `/v1/users/${member.userId}/password`,
          body: { password },
        })
      ).data,
  })
}
